// Brief 169 §0b — JurisdictionImportModal, moved out of App.tsx.
//
// MOVED, NOT REWRITTEN. The body below (from the BUG-A SWEEP comment to the
// closing brace) is byte-identical to `src/App.tsx:658-957@105f249a`; the
// function itself, `:664-957@105f249a`, hashes
//   sha256 5bc2c8779d5f4f620657cd334044adbb8892cc5932f671d94ac29e2d868e54bf
// on both sides (`git show 105f249a:src/App.tsx | sed -n '664,957p'`),
// original (zero) indentation included. Only the imports above it and the
// `export default` below it are new. Do not reformat: the bytes ARE the proof
// (docs/move-integrity-standard.md §1).
//
// Hook-bearing (four `useState`), so this is a MOVE and never the
// function-call shortcut — CLAUDE.md "Bug-A sweep": "hook-bearing ones must
// be extracted, never converted". Its one mount is App.tsx's `{juImportFile &&
// <JurisdictionImportModal …/>}`, unchanged but for the import.
//
// Free names the body read in App's module scope, each rewired as an import
// and nothing else: `useState` (react), `XLSX` (xlsx), `FS` and `RAD` (lib/tokens),
// `ModalLayout` (components/SharedUI), `getJurisdictions` and
// `bulkImportJurisdictions` (api). Everything else it reads is a prop or a
// global (`String`, `Object`, `Map`, `FileReader`).

import React, { useState } from "react";
import { loadXlsx } from "../../lib/loadOnUse.mjs"; // 208 §1 — the Excel library loads the first time a sheet is read
import { FS, RAD } from "../../lib/tokens";
import { ModalLayout } from "../SharedUI";
import type { Translate } from "../../lib/interestRefusal"; // 208 §20 — the translator, typed where it is called
import { getJurisdictions, bulkImportJurisdictions } from "../../api";
import Table from "../Table";
import { withCount } from "../../lib/pluralVars"; // 209 §7 — a sentence's number, read once, as its placeholder and as `count`

// BUG-A SWEEP — the JURISDICTIONS importer, hoisted to module scope. It was
// `const JII = () => {…}` inside App's render body, mounted as <JII />. App
// re-renders on every timer tick and every SSE event, so this modal was being
// unmounted and rebuilt under the user continuously: the parsed preview and
// any error it was showing could vanish between one tick and the next.
// Third of the four importers that carried this defect.
function JurisdictionImportModal({ juImportFile, setJuImportFile, jurisdictions, setJurisdictions, t, toast }: any) {
const [parsed, setParsed] = useState<any>(null);
const [error, setError] = useState<any>(null);
const _normJ = (v: any) =>
  String(v || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
useState<any>(() => {
  const reader = new FileReader();
  reader.onload = async (ev) => {
    try {
      const XLSX = await loadXlsx(); // 208 §1 — on first use; a load that fails is the catch's words
      const wb2 = XLSX.read(ev.target!.result, { type: "array" });
      const ws2 = wb2.Sheets[wb2.SheetNames[0]];
      const json = XLSX.utils.sheet_to_json(ws2, {
        defval: "",
        raw: false,
      });
      if (!json.length) {
        setError(
          t("ju.import.empty", { fallback: "Empty file" }),
        );
        return;
      }
      const hdrs = Object.keys(json[0] as any).map(_normJ);
      if (!hdrs.includes("court")) {
        setError(
          t("ju.import.no_court_header", {
            fallback:
              "Malformed: required header COURT is missing",
          }),
        );
        return;
      }
      const existing = new Map(
        jurisdictions.map((j: any) => [_normJ(j.name), true]),
      );
      const newRows = [],
        skipped = [];
      for (const rec of json) {
        const r: any = {};
        for (const k of Object.keys(rec as any)) r[_normJ(k)] = (rec as any)[k];
        const name = String(r["court"] || "").trim();
        if (!name) continue;
        if (existing.has(_normJ(name))) {
          skipped.push(name);
          continue;
        }
        newRows.push({
          name,
          address: String(r["address"] || "").trim() || null,
          postalCode:
            String(r["postal code"] || "").trim() || null,
          city: String(r["city"] || "").trim() || null,
          tel: String(r["tel"] || "").trim() || null,
          email: String(r["email"] || "").trim() || null,
        });
        existing.set(_normJ(name), true);
      }
      setParsed({ newRows, skipped, total: json.length });
    } catch (err: any) {
      setError(
        t("ju.import.failed", {
          msg: err.message,
          fallback: "Failed: " + err.message,
        }),
      );
    }
  };
  reader.readAsArrayBuffer(juImportFile);
});
const [applying, setApplying] = useState<any>(false);
const doImport = async () => {
  if (!parsed || !parsed.newRows.length) {
    setJuImportFile(null);
    return;
  }
  if (applying) return;
  setApplying(true);
  try {
    /* Each row is a fresh literal built in the parse loop — no shared refs. Hand to server, which runs createMany inside a single transaction (idempotent on COURT name). */ const rowsPayload =
      parsed.newRows.map((r: any) => ({
        name: r.name,
        address: r.address,
        postalCode: r.postalCode,
        city: r.city,
        tel: r.tel,
        email: r.email,
      }));
    console.log(
      "[ju.bulk-import] sending",
      rowsPayload.length,
      "rows",
    );
    const result = (await bulkImportJurisdictions(rowsPayload)) as { created: number; skipped?: unknown[] };
    console.log(
      "[ju.bulk-import] created",
      result && result.created,
      "skipped",
      result && (result.skipped || []).length,
    );
    const fresh = await getJurisdictions();
    setJurisdictions(fresh);
    // 208 §20 [RULED 16] — one sentence per number (imported, skipped), each in its language's plural, joined here
    const tr = t as Translate;
    const skippedN = (result.skipped ?? []).length + Number(parsed.skipped.length);
    toast(
      [
        tr("ju.import.done_imported", { count: result.created, fallback: `Imported ${String(result.created)} courts` }),
        skippedN ? tr("ju.import.done_skipped", { count: skippedN, fallback: `skipped ${String(skippedN)}` }) : null,
      ].filter(Boolean).join(", "),
    );
    setJuImportFile(null);
  } catch (err: any) {
    toast(
      "⚠ " +
        (err.message ||
          t("ju.import.failed_generic", {
            fallback: "Import failed",
          })),
    );
    setApplying(false);
  }
};
if (error)
  return (
    <ModalLayout
      title={t("modal.import.error_title", {
        fallback: "Import Error",
      })}
      onClose={() => setJuImportFile(null)}
      showX={false}
      closeOnOverlay
      maxWidth={420}
      cancelLabel={t("common.close", { fallback: "Close" })}
    >
      <p style={{ fontSize: FS.s11, color: "var(--rd)" }}>
        {error}
      </p>
    </ModalLayout>
  );
if (!parsed)
  return (
    <ModalLayout hideHeader footer={null} maxWidth={400}>
      <p style={{ fontSize: FS.s11, color: "var(--tm)" }}>
        {t("ju.import.reading", {
          fallback: "Reading file…",
        })}
      </p>
    </ModalLayout>
  );
return (
  <ModalLayout
    title={t("modal.import.ju_title", {
      fallback: "Import Jurisdictions",
    })}
    onClose={() => setJuImportFile(null)}
    onSave={doImport}
    saveDisabled={parsed.newRows.length === 0 || applying}
    saveLabel={
      applying
        ? t("ju.import.applying", { fallback: "Importing…" })
        : t("ju.import.apply_n", {
            n: parsed.newRows.length,
            count: parsed.newRows.length, // 208 §20
            fallback: "Add " + parsed.newRows.length + " courts",
          })
    }
    showX={false}
    closeOnOverlay
    maxWidth={600}
  >
        <p style={{ fontSize: FS.s11, marginBottom: 10 }}>
          {/* 209 §7 [RULED 16] — one sentence per number, each in its language's plural */}
          {(() => {
            const tc = t as Translate; // the translator, typed where it is called (208 §20's way)
            return [
              tc("ju.import.summary_new", withCount(parsed.newRows.length, "count", { fallback: "{count} new" })),
              tc("ju.import.summary_dup", withCount(parsed.skipped.length, "count", { fallback: "{count} duplicate (skipped)" })),
              tc("ju.import.summary_total", withCount(parsed.total, "count", { fallback: "{count} rows total" })),
            ].join(", ");
          })()}
        </p>
        {parsed.skipped.length > 0 && (
          <div
            style={{
              fontSize: FS.s10,
              color: "var(--am)",
              marginBottom: 10,
              padding: "6px 8px",
              background: "var(--bi)",
              border: "1px solid var(--br)",
              borderRadius: RAD.r6,
              maxHeight: 80,
              overflowY: "auto",
            }}
          >
            <strong>
              {t("ju.import.skipped_header", {
                fallback: "Skipped (already in directory):",
              })}
            </strong>{" "}
            {parsed.skipped.slice(0, 20).join(", ")}
            {parsed.skipped.length > 20
              ? " " +
                t("app.import.more_n", withCount(parsed.skipped.length - 20, "n", { fallback: "… +{n} more",
                }))
              : ""}
          </div>
        )}
        {parsed.newRows.length > 0 && (
          <div
            className="table-wrap"
            style={{ maxHeight: 200, overflowY: "auto" }}
          >
            <Table style={{ width: "100%" }}>
              <thead>
                <tr>
                  <th
                    style={{
                      fontSize: FS.s9,
                      padding: "3px 5px",
                      textAlign: "left",
                      color: "var(--tm)",
                    }}
                  >
                    COURT
                  </th>
                  <th
                    style={{
                      fontSize: FS.s9,
                      padding: "3px 5px",
                      textAlign: "left",
                      color: "var(--tm)",
                    }}
                  >
                    CITY
                  </th>
                  <th
                    style={{
                      fontSize: FS.s9,
                      padding: "3px 5px",
                      textAlign: "left",
                      color: "var(--tm)",
                    }}
                  >
                    TEL
                  </th>
                </tr>
              </thead>
              <tbody>
                {parsed.newRows.slice(0, 8).map((r: any, i: any) => (
                  <tr key={i}>
                    <td
                      style={{
                        fontSize: FS.s10,
                        padding: "2px 5px",
                        maxWidth: 280,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {r.name}
                    </td>
                    <td
                      style={{ fontSize: FS.s10, padding: "2px 5px" }}
                    >
                      {r.city || "—"}
                    </td>
                    <td
                      style={{ fontSize: FS.s10, padding: "2px 5px" }}
                    >
                      {r.tel || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
  </ModalLayout>
);
}

export default JurisdictionImportModal;
